<script>
import Input from "./types/input";
import Select from "./types/select";
import DateTime from "./types/date";
import InputRange from "./types/inputrange";
import Remote from "./types/remote";
import { isEmpt } from "@/utils/utils";

// 支持的搜索组件类型，后续增加日期类型
const ColTypes = ["text", "enum", "datetime", "inputrange", "remote"];

export default {
  components: { Input, Select, DateTime, InputRange },
  props: {
    searchCols: {
      type: Array,
      default: () => [],
    },
    searchParams: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
    defaultShowMore: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showMore: this.defaultShowMore,
      animation: false,
    };
  },
  computed: {
    isMore() {
      return this.searchCols.length > 4;
    },
    searchColsFirstRow() {
      return this.isMore ? this.searchCols.slice(0, 2) : this.searchCols;
    },
    searchColsSecondRow() {
      return this.isMore
        ? this.searchCols.slice(2, this.searchCols.length)
        : [];
    },
  },
  created() {
    this.initParams().catch(() => {
      this.$emit("error");
    });
  },
  methods: {
    onShowMore() {
      this.animation = true;
      this.showMore = !this.showMore;
    },
    dealOptionItem(options) {
      return options.map((item) => {
        if (typeof item === "object") {
          return item;
        } else {
          return {
            text: item,
            value: item,
          };
        }
      });
    },
    async dealOptions(items, ...params) {
      return new Promise((resolve, reject) => {
        if (Array.isArray(items)) {
          resolve(this.dealOptionItem(items));
        } else {
          const res = items(...params);
          if (Array.isArray(res)) {
            resolve(this.dealOptionItem(res));
          } else {
            res
              .then((data) => {
                resolve(this.dealOptionItem(data));
              })
              .catch(reject);
          }
        }
      });
    },
    /**
     * 如果items中没有当前选中的值,清空当前选项
     * 单选场景下,如果clearable!==true,说明当前项为必填,需要设置第一项为初始值
     */
    dealOptionInitValue(col) {
      const { items, dataIndex } = col;
      const val = this.searchParams[dataIndex];
      // items长度为0的时候直接清空
      if (items.length === 0) {
        if (Array.isArray(val)) {
          this.searchParams[dataIndex] = [];
        } else {
          this.searchParams[dataIndex] = undefined;
        }
      } else {
        // 当前为多选，选中的值为数组且长度>0；或者单选，值非空
        if (Array.isArray(val)) {
          if (val.length > 0) {
            for (let i = val.length - 1; i >= 0; i--) {
              const flag = items.some((item) => {
                return item.value === val[i];
              });
              if (!flag) {
                val.splice(i, 1);
              }
            }
          }
        } else {
          if (!isEmpt(val)) {
            const flag = items.some((item) => {
              return item.value === val;
            });
            if (!flag) {
              this.searchParams[dataIndex] = undefined;
              if (col.clearable !== true) {
                this.dealEnumValue(col);
              }
            }
          }
        }
      }
    },

    /**
     * 处理下拉类型的value,items长度大于0时默认第一
     * multiple=true的时候设置的array格式
     * valueType为object的时候需要设置value为object
     */
    dealEnumValue(col) {
      if (col.items.length > 0) {
        if (col.multiple) {
          this.searchParams[col.dataIndex] =
            col.valueType === "object" ? [col.items[0]] : [col.items[0].value];
        } else {
          this.searchParams[col.dataIndex] =
            col.valueType === "object" ? col.items[0] : col.items[0].value;
        }
      } else {
        if (col.multiple) {
          this.searchParams[col.dataIndex] = [];
        } else {
          this.searchParams[col.dataIndex] = undefined;
        }
      }
    },
    // 根据type属性获取列的类型，如果不指定，返回默认类型
    getColType(oCol) {
      return ColTypes.includes(oCol.type) ? oCol.type : ColTypes[0];
    },

    async initParams() {
      const searchParams = this.searchParams;
      for (let key in searchParams) {
        delete searchParams[key];
      }
      // 初始化searchParams完成双向绑定，下面的循环存在同步代码，所以这里单独循环处理
      for (let col of this.searchCols) {
        this.$set(searchParams, col.dataIndex, col.initValue);
      }

      // 处理searchCols
      for (let col of this.searchCols) {
        if (col.type === "enum") {
          const items = col.items;
          if (Object.prototype.toString.call(items) === "[object Function]") {
            // 这里还可以优化，存在initValue的时候可以异步调用，后面优化
            col.items = await this.dealOptions(items);
          } else {
            col.items = this.dealOptionItem(items);
          }
          // 不存在initValue的时候,需要选取第一项为默认值
          if (!Object.prototype.hasOwnProperty.call(col, "initValue")) {
            this.dealEnumValue(col);
          } else {
            this.dealOptionInitValue(col);
          }
          if (col.onChange) {
            let linkedCols = this.searchCols.filter(
              (item) => !!col.onChange[item.dataIndex]
            );
            for (let linkedCol of linkedCols) {
              linkedCol.items = await this.dealOptions(
                col.onChange[linkedCol.dataIndex],
                this.searchParams[col.dataIndex],
                linkedCol
              );
              // 不存在initValue的时候,需要选取第一项为默认值
              if (
                !Object.prototype.hasOwnProperty.call(linkedCol, "initValue")
              ) {
                this.dealEnumValue(linkedCol);
              } else {
                this.dealOptionInitValue(col);
              }
            }
          }
        }
      }
      this.$emit("search", this.searchParams);
    },

    isShowSearchBtn() {
      // 没有搜索项时不显示搜索按钮
      if (this.searchCols.length === 0) {
        return false;
      }

      // 搜索项都是自动刷新时不显示搜索按钮
      if (!this.searchCols.some((item) => !item.refresh)) {
        return false;
      }

      return true;
    },

    // 搜索按钮的点击处理
    onSearch() {
      console.log("search:", this.searchParams);
      this.$emit("search", this.searchParams);
    },

    // 用户输入的搜索条件有变化时的处理。如果不是实现搜索，该方法可以为空
    async onSearchChange(oCol, val) {
      // 如果有onChange, 会通知各依赖的列进行处理
      if (oCol.onChange) {
        let linkedCols = this.searchCols.filter(
          (item) => !!oCol.onChange[item.dataIndex]
        );
        for (let item of linkedCols) {
          item.items = await this.dealOptions(
            oCol.onChange[item.dataIndex],
            this.searchParams[oCol.dataIndex],
            item
          );
          this.dealEnumValue(item);
        }
      }

      if (oCol.onSelect) {
        oCol.onSelect(val);
      }

      // refresh=true时输入值变化后需要刷新列表
      if (oCol.refresh) {
        this.onSearch();
      }
    },
    renderFormItem(col) {
      const type = this.getColType(col);
      if (type === "text") {
        return (
          <Input
            v-model={this.searchParams[col.dataIndex]}
            placeholder={col.placeholder}
            clearable={col.clearable}
            vOn:change={(val) => this.onSearchChange(col, val)}
            vOn:keyup_enter_native={this.onSearch}
          />
        );
      } else if (type === "enum") {
        return (
          <Select
            v-model={this.searchParams[col.dataIndex]}
            filterable={col.filterable}
            placeholder={col.placeholder}
            value-type={col.valueType}
            clearable={col.clearable}
            multiple={col.multiple}
            items={col.items}
            vOn:change={(val) => this.onSearchChange(col, val)}
          />
        );
      } else if (type === "datetime") {
        return (
          <DateTime
            v-model={this.searchParams[col.dataIndex]}
            format={col.format}
            clearable={col.clearable}
            shortcuts={col.shortcuts}
            vOn:change={(val) => this.onSearchChange(col, val)}
          />
        );
      } else if (type === "inputrange") {
        return (
          <InputRange
            v-model={this.searchParams[col.dataIndex]}
            separator={col.separator}
            clearable={col.clearable}
            suffix={col.suffix}
            vOn:change={(val) => this.onSearchChange(col, val)}
            vOn:keyup_enter_native={this.onSearch}
          />
        );
      } else if (type === "remote") {
        return (
          <Remote
            v-model={this.searchParams[col.dataIndex]}
            placeholder={col.placeholder}
            multiple={col.multiple}
            clearable={col.clearable}
            remoteMethod={col.remoteMethod}
            vOn:change={(val) => this.onSearchChange(col, val)}
          />
        );
      }
    },
  },
  render() {
    return (
      <el-form
        inline={true}
        nativeOn={{
          submit: (e) => e.preventDefault(),
        }}
        class="demo-form-inline"
      >
        <div>
          {this.searchColsFirstRow.map((col) => {
            return (
              <el-form-item key={col.dataIndex} label={col.label}>
                {this.renderFormItem(col)}
              </el-form-item>
            );
          })}
          {this.isShowSearchBtn() && (
            <el-form-item>
              <el-button-group>
                <el-button
                  icon="el-icon-search"
                  type="primary"
                  data-cy="cs_table_search"
                  v-debounce-click={this.onSearch}
                >
                  搜索
                </el-button>
                {this.isMore && (
                  <el-button
                    type="primary"
                    icon="el-icon-arrow-down"
                    style="padding-left:5px;padding-right:5px;"
                    vOn:click={this.onShowMore}
                  ></el-button>
                )}
              </el-button-group>
            </el-form-item>
          )}
        </div>
        <div
          class={{
            show: this.showMore,
            "show-slide": this.showMore && this.animation,
            hide: !this.showMore,
            "search-bar-second-row": true,
          }}
        >
          {this.searchColsSecondRow.map((col) => {
            return (
              <el-form-item key={col.dataIndex} label={col.label}>
                {this.renderFormItem(col)}
              </el-form-item>
            );
          })}
        </div>
      </el-form>
    );
  },
};
</script>

<style lang="scss" scoped>
@keyframes slideInDown {
  0% {
    opacity: 0;
    height: 0;
  }
  100% {
    height: 33px;
  }
}
.search-bar-second-row {
  margin-top: 10px;
  &.show {
    display: block;
  }

  &.show-slide {
    animation: slideInDown 300ms;
  }

  &.hide {
    display: none;
  }
}
</style>
